import type { ProductFormulation } from '@/constants/products';
import type { DosageOrigin } from './extractMaxDailyDose';
import { extractMaxDailyDose } from './extractMaxDailyDose';
import { getFormulationUnit } from './getFormulationUnit';
import { removeMonthlyDispenseLimitPhrase } from './getMonthlyDispenseLimitPhrase';
import { supportsDispenseLimit } from './supportsDispenseLimit';
import { supportsMaxDailyDose } from './supportsMaxDailyDose';

/** The pad rule the prefill follows; the pad's own rules satisfy it */
type DosagePrefillRules = { enableDispenseLimits: boolean };

type DosagePrefill = {
  dosage: string | undefined;
  maxDailyDose: number | undefined;
};

/**
 * Dosage fields for a pad item that takes its dosage from a product or a previous prescription. With dispense
 * limits off only a monthly dispense limit clause comes out of the text; the maximum daily dose stays in it.
 * `origin` says where the text came from, for a log about it.
 */
export const getDosagePrefill = (
  dosage: string | undefined,
  formulation: ProductFormulation | undefined,
  rules: DosagePrefillRules,
  origin?: DosageOrigin,
): DosagePrefill => {
  // A reissued script's dosage ends with the monthly dispense limit it was written with, worked out from that script's
  // quantity, so it is never carried into a new one, flag on or off; the review step writes the limit in force afresh
  const withoutMonthlyLimit =
    dosage !== undefined && formulation !== undefined && supportsDispenseLimit(formulation)
      ? removeMonthlyDispenseLimitPhrase(dosage)
      : dosage;
  // maxDailyDose is always set so a product swap never carries the previous product's value over
  if (!rules.enableDispenseLimits || withoutMonthlyLimit === undefined || formulation === undefined) {
    return { dosage: withoutMonthlyLimit, maxDailyDose: undefined };
  }
  if (!supportsMaxDailyDose(formulation)) {
    return { dosage: withoutMonthlyLimit, maxDailyDose: undefined };
  }
  return extractMaxDailyDose(withoutMonthlyLimit, getFormulationUnit(formulation), origin);
};
